import { Component, EventEmitter, Input, OnInit, Output } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { PlatformEnum, PlatformService } from '@panamax/app-state';
import { BaseDualPaneModalContainerComponent } from '@shared/components/modal-with-nav-inline/dual-pane-container/base-dual-pane-modal-container/base-dual-pane-modal-container.component';
import {
  dualPaneBasicSelection,
  dualPaneDefaultVerifySave,
  fileSaveOnNavigation,
  getSearchableObservable$,
  handleInputOrRangeValueSave,
  singleSelectRadioSearchDefault,
  trimSpaceTo30,
  updateRightPaneWithValue,
  updateRightPaneWithValueForMaps,
} from '@shared/components/modal-with-nav-inline/dual-pane-container/Helpers/dual-pane-helper-functions';
import {
  DualPaneView,
  MultiInputItems,
  MultiSelectItems,
} from '@shared/constants/dual-pane.enum';
import { b64toBlob } from '@shared/helpers/file.helpers';
import {
  BasicListItem,
  DualPaneFlagsConfig,
  DualPaneFunctionsConfig,
  DualPaneLabelsConfig,
  DualPaneViewModel,
  InputLabelItems,
  LeftPaneInfo,
  RightPaneInfo,
} from '@shared/models/dual-pane-model';
import {
  CSV_LIMITED_EXTENSIONS,
  XLSX_LIMITED_EXTENSIONS,
} from '@shared/services/file/model/file.model';
import { BehaviorSubject, firstValueFrom, of, Subject } from 'rxjs';
import { PickedFile } from '@capawesome/capacitor-file-picker';
import { DocumentImportService } from '@shared/services/import-documents/document-import.service';
import {
  BAD_PRODUCT_NUMBER,
  ImportListMapOptions,
  ListTypes,
} from '@shared/constants/lists-constants';
import { ImportCsvRow } from '@usf/list-types';
import { ListsPageData } from '@app/lists/model/lists-page.model';
import { doesListNameExist } from '@app/lists/helpers/lists-checks-helpers';
import { UserKinds } from '@usf/user-types/user';
import { read, utils } from 'xlsx';
import emojiRegex from 'emoji-regex';

@Component({
  selector: 'app-import-list-modal-container',
  templateUrl: './import-list-modal-container.component.html',
  styleUrls: ['./import-list-modal-container.component.scss'],
})
export class ImportListModalContainerComponent extends BaseDualPaneModalContainerComponent {
  @Input() translateService: TranslateService;
  @Input() listPageData: ListsPageData;
  @Output() listTemplateEvent = new EventEmitter<void>();

  fileISValidOrInvalid = new Subject<boolean>();
  fileData = [];

  constructor(
    public platformService: PlatformService,
    public documentImportService: DocumentImportService,
  ) {
    super(platformService);
  }

  listTypes = [
    {
      label: 'i18n.lists.public',
      value: ListTypes.public,
      selected: true,
      type: MultiSelectItems.basic,
    },
    {
      label: 'i18n.lists.internal',
      value: ListTypes.internal,
      selected: false,
      type: MultiSelectItems.basic,
    },
    {
      label: 'i18n.lists.private',
      value: ListTypes.private,
      selected: false,
      type: MultiSelectItems.basic,
    },
  ];

  createImportListVM = () => {
    const labelConfig: DualPaneLabelsConfig = {
      dataCy: 'import-list',
      title: 'i18n.lists.importListModal.title',
      submitText: this.translateService.instant('i18n.common.submit'),
      backText: this.translateService.instant('i18n.common.save'),
    };
    const functionConfig: DualPaneFunctionsConfig = {
      intialDesktopTabletView: this.buildImportListRightPane,
      handleSubmit: this.submitModalValues,
      verifySubmit: this.verifySubmission,
    };

    const flagConfig: DualPaneFlagsConfig = {};

    const contentItems = [
      {
        label: 'i18n.lists.importListModal.chooseFile',
        placeHolder: 'i18n.lists.importListModal.chooseFileSubHeader',
        view: DualPaneView.filePicker,
        selected: this.platformService.platformType !== PlatformEnum.mobile,
        value: undefined,
        onSelectfunction: this.buildImportListRightPane,
      },
      {
        label: 'i18n.lists.importListModal.chooseName',
        placeHolder: 'i18n.lists.importListModal.chooseNameSubHeader',
        view: DualPaneView.multiInput,
        selected: false,
        value: undefined,
        onSelectfunction: this.buildNewNameRightPane,
      },
    ];

    if (this.listPageData.userKind === UserKinds.Internal) {
      contentItems.push({
        label: 'i18n.lists.importListModal.chooseType',
        placeHolder: 'i18n.lists.public',
        view: DualPaneView.searchableItemRadio,
        selected: false,
        value: ListTypes.public,
        onSelectfunction: this.buildListTypeRightPane,
      });
    }

    this.buildBasePaneWithConfigs(
      labelConfig,
      functionConfig,
      flagConfig,
      contentItems,
    );
  };

  buildImportListRightPane = (
    viewModel: DualPaneViewModel,
    paneSelected: string,
  ) => {
    const fileTitle =
      this.listPageData.userKind === UserKinds.Internal
        ? 'i18n.lists.importListModal.chooseFileInternalUserText'
        : 'i18n.lists.importListModal.chooseFileExternalUserText';
    const fileModal = {
      view: DualPaneView.filePicker,
      title: 'i18n.lists.importListModal.chooseFile',
      verifySaveButton: this.verifyFile,
      overrideSaveOnNavigation: fileSaveOnNavigation,
      filePane: {
        fileTitle,
        showExtraInfo: true,
        infoLabel: 'i18n.lists.importListModal.downloadTemplate',
        requestInfo: this.requestInfoForFile,
        fileTypes: [...CSV_LIMITED_EXTENSIONS, ...XLSX_LIMITED_EXTENSIONS],
        verifyContent: this.verifyDownloadedFile,
        fileSelected: this.importFileSelecteed,
        showSpinner: false,
      },
      errorMessage: 'i18n.lists.importListModal.fileError',
    } as RightPaneInfo;
    updateRightPaneWithValue(viewModel, paneSelected, fileModal);
    return fileModal;
  };

  // ignore the actual file and grab the fileData saed instead
  importFileSelecteed = (
    left: LeftPaneInfo,
    platform: PlatformEnum,
    right: RightPaneInfo,
    file: File,
  ) => {
    left.content = left.content.map(item => {
      if (item.selected) {
        return { ...item, subLabel: file?.name ?? '', value: file };
      } else {
        return item;
      }
    });
  };

  verifyDownloadedFile = async (file: File, viewModel: DualPaneViewModel) => {
    const blob = file;
    const fileReader = new FileReader();
    // Determine if the file is an Excel file
    const isExcelFile =
      file.name.endsWith('.xlsx') || file.name.endsWith('.xls');

    fileReader.onload = async ev => {
      if (isExcelFile) {
        // Convert Excel to CSV
        const arrayBuffer = ev.target.result;
        const workbook = read(arrayBuffer, { type: 'array' });
        const sheetName = workbook.SheetNames[0];
        const worksheet = workbook.Sheets[sheetName];
        let csvData = utils.sheet_to_csv(worksheet);

        // Process the CSV data
        await this.buildCsvCallBack({ currentTarget: { result: csvData } });
      } else {
        // Process the CSV data directly
        await this.buildCsvCallBack(ev);
      }
    };

    this.fileISValidOrInvalid = new Subject<boolean>();

    if (isExcelFile) {
      fileReader.readAsArrayBuffer(blob);
    } else {
      fileReader.readAsText(blob);
    }

    const result = await firstValueFrom(this.fileISValidOrInvalid);

    if (!result) {
      this.fileData = [];
    }
    return result;
  };

  buildCsvCallBack = async (ev: any) => {
    let csvData: string = ev.currentTarget.result;
    // Emoji cleanup: Replace all emojis with '??'
    const emojiPattern = emojiRegex();
    csvData = csvData.replace(emojiPattern, '??');
    const checkArray = csvData.split(',');
    let validFile = false;
    // The minmum amount of csv columns to be a valid file read
    if (checkArray.length > 5) {
      for (let i = 0; i < 5; i++) {
        if (checkArray[i]?.toLowerCase().includes('product number')) {
          validFile = true;
        }
      }
    }

    if (validFile) {
      let csvArray = (await this.documentImportService.breakDownCSV(
        csvData,
      )) as string[][];

      // reduce down to just product number, group name, product note
      const objectsforList =
        this.documentImportService.trimCSVArraytoJustDesiredColumns(
          ['product number'],
          csvArray,
          ['group name', 'product note'],
        );
      if (objectsforList.length === 0) {
        validFile = false;
      }

      if (validFile) {
        // converts the papa parse data to a usable object for our ngrx layer
        const csvRows: ImportCsvRow[] =
          await this.convertStringsToImportRows(objectsforList);
        if (csvRows.length === 0) {
          validFile = false;
        }

        if (validFile) {
          this.fileISValidOrInvalid.next(true);
          this.fileData = csvRows;
        }
      }
    }

    if (!validFile) {
      this.fileISValidOrInvalid.next(false);
    }
  };

  convertStringsToImportRows = async (objectsforList: string[][]) => {
    let foundProductNummber = false;
    return objectsforList
      .filter(obj => {
        // filter out rows that do not have productNumbers, once we hit first valid productNumber then stop filtering.
        if (!foundProductNummber) {
          foundProductNummber = this.isNumber(obj['product number']);
          return foundProductNummber;
        }
        // filter out empty rows;
        return !(
          (obj['group name'] === undefined || obj['group name'] === '') &&
          (obj['product note'] === undefined || obj['product note'] === '') &&
          (obj['product number'] === undefined ||
            Number(obj['product number']) === 0)
        );
      })
      .map(obj => {
        let importRow = {} as ImportCsvRow;
        if (obj['group name']) importRow.groupName = obj['group name'];

        if (obj['product note']) importRow.productNote = obj['product note'];

        if (obj['row']) importRow.rowNumber = obj['row'];

        importRow.productNumber = this.isNumber(obj['product number'])
          ? Number(obj['product number'])
          : BAD_PRODUCT_NUMBER;

        return importRow;
      });
  };

  // empty strig comes back as 0
  isNumber(value: string) {
    const numberForCheck = Number(value);
    return !Number.isNaN(numberForCheck) && numberForCheck !== 0;
  }

  requestInfoForFile = () => {
    this.listTemplateEvent.emit();
  };

  verifyFile = (vm: DualPaneViewModel) => {
    return !!vm.rightView.value && this.fileData.length > 0;
  };

  buildNewNameRightPane = (
    viewModel: DualPaneViewModel,
    paneSelected: string,
  ) => {
    let inputValue = undefined;
    if (!!viewModel?.leftView?.content[1].value) {
      inputValue = viewModel?.leftView?.content[1].value.get(0);
    }
    const nameModal = {
      view: DualPaneView.multiInput,
      title: 'i18n.lists.importListModal.chooseName',
      inputsPane: {
        items: [
          {
            type: MultiInputItems.input,
            label: 'i18n.lists.newListName',
            placeHolder1: 'i18n.lists.importListModal.enter',
            inputcleanup: trimSpaceTo30,
            maxChar: 30,
            errorMessage: 'i18n.lists.importListModal.unique',
            hasError: false,
            onInput: this.onNameInput,
            inputValue,
          },
        ],
        handleValueUpdate: handleInputOrRangeValueSave,
      },
      verifySaveButton: this.verifyNewName,
      overrideSaveOnNavigation: this.saveNewName,
    } as RightPaneInfo;
    updateRightPaneWithValueForMaps(viewModel, paneSelected, nameModal);
    return nameModal;
  };

  onNameInput = (item: InputLabelItems, value: any) => {
    item.inputValue = !!item.inputcleanup ? item.inputcleanup(value) : value;
    item.hasError = doesListNameExist(this.listPageData, item.inputValue ?? '');
    return item.inputValue;
  };

  verifyNewName = (vm: DualPaneViewModel) => {
    return (
      !!vm?.rightView.value?.get(0) &&
      !doesListNameExist(this.listPageData, vm?.rightView.value?.get(0) ?? '')
    );
  };

  handleNewName = (
    vm: DualPaneViewModel,
    pane: RightPaneInfo,
    value: any,
    index: number,
  ) => {
    if (!pane.value) {
      pane.value = new Map<number, any>();
    }
    pane.value.set(index, value);
    let subLabel = '';
    if (value) {
      subLabel = value;
    }

    vm.leftView.content = vm.leftView.content.map(item => {
      if (item.selected) {
        return { ...item, subLabel, value: pane.value };
      } else {
        return item;
      }
    });
  };

  saveNewName = (vm: DualPaneViewModel, item: BasicListItem, value: any) => {
    let subLabel = '';
    if (value) {
      subLabel = value.get(0);
    }
    item.value = value;
    item.subLabel = subLabel;
    return item;
  };

  buildListTypeRightPane = (
    viewModel: DualPaneViewModel,
    paneSelected: string,
  ) => {
    const listTypeModal = {
      view: DualPaneView.searchableItemRadio,
      title: 'i18n.lists.importListModal.chooseType',
      verifySaveButton: dualPaneDefaultVerifySave,
      searchPane: {
        hideSearch: true,
        onSearchFunction: singleSelectRadioSearchDefault,
        searchBehavior: new BehaviorSubject<string>(''),
        searchKey: '',
        searchPlaceholder: '',
      },
      itemsObservable$: of(this.listTypes),
      handleItemSelection: dualPaneBasicSelection,
    } as RightPaneInfo;
    listTypeModal.searchPane.searchableObservable$ =
      getSearchableObservable$(listTypeModal);
    updateRightPaneWithValue(viewModel, paneSelected, listTypeModal);
    return listTypeModal;
  };

  verifySubmission = (vm: DualPaneViewModel) => {
    let valid = true;
    vm.leftView.content.forEach((item, index) => {
      switch (index) {
        case 0:
          if (this.fileData.length === 0 || !item.value) {
            valid = false;
          }
          break;
        case 1:
          if (!item.value) {
            valid = false;
          }
          break;
        case 2:
          if (!item.value) {
            valid = false;
          }
          break;
      }
    });
    return valid;
  };

  async openModal() {
    await this.createImportListVM();
    this.isModalOpen$.next(true);
  }

  closeModal = () => {
    this.isModalOpen$.next(false);
  };

  submitModalValues = async (vm: DualPaneViewModel) => {
    const submitMap = new Map<string, any | any[]>();
    const fileData = this.fileData;
    submitMap.set(ImportListMapOptions.fileSelect, fileData);
    submitMap.set(
      ImportListMapOptions.listName,
      vm.leftView.content[1]?.value.get(0),
    );
    if (this.listPageData.userKind === UserKinds.Internal) {
      submitMap.set(
        ImportListMapOptions.listType,
        vm.leftView.content[2]?.value,
      );
    } else {
      submitMap.set(ImportListMapOptions.listType, ListTypes.public);
    }

    this.submit$.next(submitMap);
    this.closeModalAndSubmit();
  };

  closeModalAndSubmit() {
    this.isModalOpen$.next(false);
    if (this.submit$.value !== undefined) {
      this.submitEvent.emit(this.submit$.value);
    }
  }
}
